import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  CaretDownIcon,
  CaretUpIcon,
  CheckCircleIcon,
  EyeSlashIcon,
} from "@phosphor-icons/react";

import Layout from "../../components/Layout";
import EntityHeader from "../../components/crm/EntityHeader";
import CopyButton from "../../components/CopyButton";
import {
  ErrorBanner,
  PrimaryButton,
  SecondaryButton,
} from "../../components/crm/Form";
import SeverityDot from "../../components/monitoring/SeverityDot";
import AlertStatusBadge from "../../components/monitoring/AlertStatusBadge";
import { formatApiError } from "../../lib/api";
import {
  ALERT_TYPE_LABEL,
  SEVERITY_TO_SR_PRIORITY,
  VENDOR_LABEL,
  acknowledgeAlert,
  getAlert,
  ignoreAlert,
  isoLocal,
  linkServiceRequest,
  relativeTime,
  resolveAlert,
} from "../../lib/monitoring";
import { createServiceRequest } from "../../lib/serviceRequests";
import { useAuth } from "../../contexts/AuthContext";

export default function MonitoringDetail() {
  const { alertId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isOffice = user?.role === "owner" || user?.role === "ops";
  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [rawOpen, setRawOpen] = useState(false);
  const [createSrOpen, setCreateSrOpen] = useState(false);
  const [createSrBusy, setCreateSrBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const data = await getAlert(alertId);
      setAlert(data);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [alertId]);

  useEffect(() => {
    load();
  }, [load]);

  const action = async (fn, label) => {
    setBusy(true);
    try {
      const updated = await fn(alertId);
      setAlert(updated);
      toast.success(label);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const onCreateAndLinkSR = async () => {
    if (!alert) return;
    setCreateSrBusy(true);
    try {
      const created = await createServiceRequest({
        customer_id: alert.customer_id,
        property_id: alert.property_id,
        intake_source: "internal",
        issue_summary: (alert.title || "").slice(0, 300),
        issue_details: alert.description || null,
        priority: SEVERITY_TO_SR_PRIORITY[alert.severity] || "normal",
        requested_service_type: "diagnostic",
      });
      const linked = await linkServiceRequest(alertId, created.id);
      setAlert(linked);
      setCreateSrOpen(false);
      toast.success("Service request created and linked");
    } catch (e) {
      toast.error(formatApiError(e));
      // Leave the inline form open so ops can retry.
    } finally {
      setCreateSrBusy(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6 animate-pulse">
          <div className="h-8 w-64 bg-slate-100 rounded" />
          <div className="grid lg:grid-cols-[1fr_360px] gap-6">
            <div className="space-y-4">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="h-32 bg-slate-100 rounded-md border border-slate-200" />
              ))}
            </div>
            <div className="space-y-4">
              {[0, 1].map((i) => (
                <div key={i} className="h-28 bg-slate-100 rounded-md border border-slate-200" />
              ))}
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (err || !alert) {
    return (
      <Layout>
        <ErrorBanner message={err} />
        <button
          type="button"
          onClick={() => navigate("/monitoring")}
          className="text-sm text-slate-700 hover:text-slate-900 font-semibold"
        >
          ← Back to alerts
        </button>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <EntityHeader
          title={alert.title}
          subtitle={`Alert · ${ALERT_TYPE_LABEL[alert.alert_type] || alert.alert_type}`}
          meta={
            <>
              <span className="inline-flex items-center gap-1.5">
                id · {alert.id.slice(0, 8)}
                <CopyButton
                  value={alert.id}
                  label="Alert ID"
                  testId="alert-copy-id"
                />
              </span>
              <span>vendor · {alert.vendor}</span>
            </>
          }
        />

        <div className="grid lg:grid-cols-[1fr_360px] gap-6">
          <div className="space-y-4">
            {/* Header card with big severity dot */}
            <section
              data-testid="alert-header-card"
              className="bg-white border border-slate-200 rounded-md p-5 flex items-start gap-4"
            >
              <SeverityDot severity={alert.severity} size={20} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                    {alert.severity}
                  </span>
                  <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">·</span>
                  <span className="text-[10px] uppercase tracking-wider font-bold text-amber-700 bg-amber-100 border border-amber-300 rounded px-1.5 py-0.5">
                    {ALERT_TYPE_LABEL[alert.alert_type] || alert.alert_type}
                  </span>
                  <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">·</span>
                  <span className="text-[10px] uppercase tracking-wider font-bold text-slate-700">
                    {VENDOR_LABEL[alert.vendor] || alert.vendor}
                  </span>
                </div>
                <h1 className="font-heading text-2xl font-bold text-slate-900 mt-1.5">
                  {alert.title}
                </h1>
                <div className="text-xs text-slate-500 mt-1" title={alert.created_at}>
                  Received {relativeTime(alert.created_at)} ({isoLocal(alert.created_at)})
                </div>
              </div>
            </section>

            {/* System info */}
            <section className="bg-white border border-slate-200 rounded-md p-5">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-2">
                System
              </div>
              {alert.system_id ? (
                <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                  <Row label="Customer" value={
                    alert.customer_id ? (
                      <button
                        type="button"
                        onClick={() => navigate(`/customers/${alert.customer_id}`)}
                        className="text-slate-900 hover:text-amber-700 underline-offset-2 hover:underline text-left"
                      >
                        {alert.customer_name || alert.customer_id}
                      </button>
                    ) : "—"
                  } />
                  <Row label="Property" value={
                    alert.property_id ? (
                      <button
                        type="button"
                        onClick={() => navigate(`/properties/${alert.property_id}`)}
                        className="text-slate-900 hover:text-amber-700 underline-offset-2 hover:underline text-left"
                      >
                        {alert.property_address || alert.property_id}
                      </button>
                    ) : "—"
                  } />
                  <Row label="Solar system" value={
                    <button
                      type="button"
                      onClick={() => navigate(`/solar-systems/${alert.system_id}`)}
                      className="text-slate-900 hover:text-amber-700 underline-offset-2 hover:underline text-left"
                    >
                      {alert.system_label || alert.system_id}
                    </button>
                  } />
                </dl>
              ) : (
                <div className="text-sm text-slate-400 italic">No system linked</div>
              )}
            </section>

            {/* Description */}
            <section className="bg-white border border-slate-200 rounded-md p-5">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-2">
                Description
              </div>
              <div className="text-sm text-slate-800 whitespace-pre-wrap">
                {alert.description || <span className="text-slate-400 italic">No description</span>}
              </div>
              {alert.raw_payload && (
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={() => setRawOpen((o) => !o)}
                    data-testid="alert-raw-toggle"
                    className="flex items-center gap-1 text-[11px] uppercase tracking-wider text-slate-600 font-bold hover:text-slate-900"
                  >
                    {rawOpen ? <CaretUpIcon size={12} /> : <CaretDownIcon size={12} />}
                    Raw vendor payload
                  </button>
                  {rawOpen && (
                    <pre className="mt-2 text-[11px] font-data bg-slate-50 border border-slate-200 rounded p-3 overflow-auto max-h-96">
                      {JSON.stringify(alert.raw_payload, null, 2)}
                    </pre>
                  )}
                </div>
              )}
            </section>

            {/* Linked records */}
            <section
              data-testid="alert-linked-records-card"
              className="bg-white border border-slate-200 rounded-md p-5"
            >
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-2">
                Linked records
              </div>
              {alert.service_request_id || alert.diagnostic_report_id ? (
                <ul className="space-y-1.5 text-sm">
                  {alert.service_request_id && (
                    <li>
                      <button
                        type="button"
                        onClick={() => navigate(`/service-requests/${alert.service_request_id}`)}
                        data-testid="alert-linked-sr"
                        className="text-slate-900 hover:text-amber-700 underline-offset-2 hover:underline"
                      >
                        Service request {alert.service_request_id.slice(0, 8)}
                      </button>
                    </li>
                  )}
                  {alert.diagnostic_report_id && (
                    <li>
                      <span className="text-slate-700">
                        Diagnostic report {alert.diagnostic_report_id.slice(0, 8)}
                      </span>
                    </li>
                  )}
                </ul>
              ) : (
                <CreateSRBranch
                  alert={alert}
                  isOffice={isOffice}
                  open={createSrOpen}
                  busy={createSrBusy}
                  onOpen={() => setCreateSrOpen(true)}
                  onCancel={() => setCreateSrOpen(false)}
                  onConfirm={onCreateAndLinkSR}
                />
              )}
            </section>

            {/* Actions taken */}
            <section className="bg-white border border-slate-200 rounded-md p-5">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-2">
                Actions taken
              </div>
              {alert.actions_taken && alert.actions_taken.length > 0 ? (
                <ul className="space-y-1.5 text-sm text-slate-800">
                  {alert.actions_taken.map((s, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-emerald-600 mt-0.5">•</span>
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-sm text-slate-400 italic">
                  No automation fired
                </div>
              )}
            </section>
          </div>

          {/* Right sidebar */}
          <aside className="space-y-4">
            <section className="bg-white border border-slate-200 rounded-md p-4">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-2">
                Status
              </div>
              <AlertStatusBadge status={alert.status} className="text-sm" />
              <div className="mt-3 space-y-2">
                {alert.status === "new" && (
                  <>
                    <PrimaryButton
                      type="button"
                      onClick={() => action(acknowledgeAlert, "Acknowledged")}
                      disabled={busy}
                      className="w-full"
                      data-testid="alert-ack-btn"
                    >
                      <CheckCircleIcon size={14} /> Acknowledge
                    </PrimaryButton>
                    <SecondaryButton
                      type="button"
                      onClick={() => action(ignoreAlert, "Ignored")}
                      disabled={busy}
                      className="w-full"
                      data-testid="alert-ignore-btn"
                    >
                      <EyeSlashIcon size={14} /> Ignore
                    </SecondaryButton>
                  </>
                )}
                {alert.status === "acknowledged" && (
                  <>
                    <PrimaryButton
                      type="button"
                      onClick={() => action(resolveAlert, "Resolved")}
                      disabled={busy}
                      className="w-full"
                      data-testid="alert-resolve-btn"
                    >
                      <CheckCircleIcon size={14} /> Resolve
                    </PrimaryButton>
                    <SecondaryButton
                      type="button"
                      onClick={() => action(ignoreAlert, "Ignored")}
                      disabled={busy}
                      className="w-full"
                      data-testid="alert-ignore-btn"
                    >
                      <EyeSlashIcon size={14} /> Ignore
                    </SecondaryButton>
                  </>
                )}
                {alert.status === "auto_ticketed" && (
                  <PrimaryButton
                    type="button"
                    onClick={() => action(resolveAlert, "Resolved")}
                    disabled={busy}
                    className="w-full"
                    data-testid="alert-resolve-btn"
                  >
                    <CheckCircleIcon size={14} /> Resolve
                  </PrimaryButton>
                )}
                {(alert.status === "resolved" || alert.status === "ignored") && (
                  <div className="text-xs text-slate-500 italic" data-testid="alert-case-complete">
                    Case complete
                  </div>
                )}
              </div>
            </section>

            <section className="bg-white border border-slate-200 rounded-md p-4 space-y-2 text-xs">
              <Row
                label="Created"
                value={<span title={alert.created_at}>{relativeTime(alert.created_at)}</span>}
              />
              <Row label="Vendor" value={VENDOR_LABEL[alert.vendor] || alert.vendor} />
              {alert.vendor_alert_id && (
                <Row label="Vendor ID" value={<span className="font-data">{alert.vendor_alert_id}</span>} />
              )}
              {alert.acknowledged_at && (
                <Row
                  label="Acknowledged"
                  value={<span title={alert.acknowledged_at}>{relativeTime(alert.acknowledged_at)}</span>}
                />
              )}
              {alert.resolved_at && (
                <Row
                  label="Resolved"
                  value={<span title={alert.resolved_at}>{relativeTime(alert.resolved_at)}</span>}
                />
              )}
            </section>
          </aside>
        </div>
      </div>
    </Layout>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
        {label}
      </span>
      <span className="text-slate-900 text-right">{value}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Phase 14.1 — Linked-records "Create SR" branch.
// Renders one of three states based on viewer role + alert linkage data.
// ---------------------------------------------------------------------------

function CreateSRBranch({ alert, isOffice, open, busy, onOpen, onCancel, onConfirm }) {
  // Cannot create an SR without a customer + property denormalized on the
  // alert (engine guarantees both come from system_id, so missing one means
  // there's no system linked).
  if (!alert.customer_id || !alert.property_id) {
    return (
      <div className="text-sm text-slate-500 italic" data-testid="alert-no-system-hint">
        Link a system to this alert to enable service request creation.
      </div>
    );
  }

  // Techs see read-only "No linked records" — no action button.
  if (!isOffice) {
    return (
      <div className="text-sm text-slate-400 italic">No linked records</div>
    );
  }

  // Owner/ops with a customer attached — show the action.
  return (
    <div className="space-y-3">
      <div className="text-sm text-slate-400 italic">No linked records</div>
      {!open && (
        <button
          type="button"
          onClick={onOpen}
          data-testid="alert-create-sr-btn"
          className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-slate-400 hover:bg-slate-50 transition"
        >
          + Create service request
        </button>
      )}
      {open && (
        <div
          data-testid="alert-sr-preview"
          className="rounded-md border border-slate-200 bg-slate-50/50 p-3 space-y-2"
        >
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
            Will create a service request with
          </div>
          <dl className="text-xs space-y-1">
            <PreviewRow label="Customer" value={alert.customer_name || "—"} />
            <PreviewRow label="Property" value={alert.property_address || "—"} />
            <PreviewRow
              label="Title"
              value={truncate(alert.title || "", 80)}
            />
          </dl>
          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onCancel}
              disabled={busy}
              data-testid="alert-create-sr-cancel"
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={busy}
              data-testid="alert-create-sr-confirm"
              className="rounded-md bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-800 disabled:opacity-40"
            >
              {busy ? "Creating…" : "Confirm — Create SR"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function PreviewRow({ label, value }) {
  return (
    <div className="flex gap-3">
      <dt className="w-20 text-[10px] uppercase tracking-wider text-slate-500 font-bold pt-0.5">
        {label}
      </dt>
      <dd className="text-slate-900 break-words flex-1">{value}</dd>
    </div>
  );
}

function truncate(s, max) {
  if (!s) return "";
  if (s.length <= max) return s;
  return s.slice(0, max).trimEnd() + "…";
}
